import React, { useState, useEffect } from 'react';
import PendingApprovalBanner from '../components/workorder/PendingApprovalBanner';
import { clientsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import RequireCap, { useCap } from '../components/auth/RequireCap';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import ClientViewDialog from '../components/client/ClientViewDialog';
import ClientTeamDialog from '../components/client/ClientTeamDialog';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Building2, Pencil, Trash2, Eye, X, UserPlus, Lock, Archive, ArchiveRestore, ShieldCheck } from 'lucide-react';

const emptyContact = {
  first_name: '', last_name: '', address: '', city: '', state: '', zip_code: '',
  phone1: '', phone2: '', email: '',
};

const emptyClient = {
  customer_number: '', name: '', date_entered: '',
  address1: '', address2: '', city: '', state: '', zip_code: '',
  contact_first_name: '', contact_last_name: '', attention: '', phone: '',
  tax_status: 'taxable', tax_exempt_id: '', terms: '', notes: '',
  winteam_customer_numbers: [], additional_contacts: [], is_active: true,
};

// Auto-suggest a Customer Number from the client name (initials for
// multi-word names, first 6 letters otherwise) — operator can overwrite.
function suggestCustomerNumber(name) {
  const words = (name || '').toUpperCase().replace(/[^A-Z0-9 ]/g, '').split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 6);
  return words.map(w => w[0]).join('').slice(0, 10);
}

export default function Clients() {
  const enginePerms = useEffectivePermissions();
  const { user } = useAuth();
  const canAdd = useCap('clients', 'add');
  const canEdit = useCap('clients', 'edit');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingClient, setViewingClient] = useState(null);
  // Supervisory teams are admin + manager territory (2026-06-26 directive).
  const canManageTeams = ['admin', 'manager'].includes(user?.role);
  const [teamOpen, setTeamOpen] = useState(false);
  const [teamClient, setTeamClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [cnTouched, setCnTouched] = useState(false);
  const [form, setForm] = useState({ ...emptyClient });

  useEffect(() => { loadClients(); }, []);

  const loadClients = async () => {
    try {
      const response = await clientsAPI.list();
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(c => {
    if (statusFilter === 'active' && c.is_active === false) return false;
    if (statusFilter === 'inactive' && c.is_active !== false) return false;
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return (
      c.name.toLowerCase().includes(q) ||
      (c.customer_number || '').toLowerCase().includes(q) ||
      (c.city || '').toLowerCase().includes(q) ||
      (c.winteam_customer_numbers || []).some(n => n.toLowerCase().includes(q))
    );
  });

  const openCreate = () => {
    setEditingId(null);
    setCnTouched(false);
    setForm({ ...emptyClient, additional_contacts: [] });
    setShowDialog(true);
  };

  const openEdit = (client) => {
    setEditingId(client.id);
    setCnTouched(true);
    setForm({
      ...emptyClient,
      ...Object.fromEntries(Object.entries(client).map(([k, v]) => [k, v ?? emptyClient[k] ?? ''])),
      is_active: client.is_active !== false,
      additional_contacts: (client.additional_contacts || []).map(c => ({ ...emptyContact, ...c })),
      winteam_customer_numbers: client.winteam_customer_numbers || [],
    });
    setShowDialog(true);
  };

  const openView = (client) => {
    setViewingClient(client);
    setShowViewDialog(true);
  };

  const editFromView = () => {
    if (viewingClient) {
      const c = viewingClient;
      setShowViewDialog(false);
      setTimeout(() => openEdit(c), 100);
    }
  };

  const setNameWithSuggest = (name) => {
    setForm(f => ({
      ...f,
      name,
      customer_number: (!editingId && !cnTouched) ? suggestCustomerNumber(name) : f.customer_number,
    }));
  };

  const updateContact = (idx, key, value) => {
    setForm(f => ({
      ...f,
      additional_contacts: f.additional_contacts.map((c, i) => i === idx ? { ...c, [key]: value } : c),
    }));
  };

  const addContact = () => setForm(f => ({ ...f, additional_contacts: [...f.additional_contacts, { ...emptyContact }] }));
  const removeContact = (idx) => setForm(f => ({ ...f, additional_contacts: f.additional_contacts.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (!form.name || !form.customer_number) {
      toast.error('Client Name and Customer Number are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        additional_contacts: form.additional_contacts.filter(c =>
          Object.entries(c).some(([k, v]) => k !== 'id' && v)
        ),
      };
      if (editingId) {
        await clientsAPI.update(editingId, payload);
        toast.success('Client updated');
      } else {
        await clientsAPI.create(payload);
        toast.success('Client created');
      }
      setShowDialog(false);
      loadClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (client) => {
    const deactivating = client.is_active !== false;
    if (deactivating) {
      let warn = `Deactivate "${client.name}"? The record and all its contacts are preserved — it just moves to the Inactive list.`;
      try {
        const { data } = await clientsAPI.usage(client.id);
        if (data.active_jobs > 0) {
          warn = `⚠️ "${client.name}" has ${data.active_jobs} ACTIVE job(s).\n\nDeactivating the client does not stop those jobs. Continue?`;
        }
      } catch { /* usage check is advisory only */ }
      if (!window.confirm(warn)) return;
    }
    try {
      await clientsAPI.setActive(client.id, !deactivating);
      toast.success(deactivating ? 'Client deactivated' : 'Client reactivated');
      loadClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Delete client "${client.name}"? This cannot be undone.`)) return;
    try {
      await clientsAPI.delete(client.id);
      toast.success('Client deleted');
      loadClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete client');
    }
  };

  // Route-level guard: deep-linking to /clients without the view capability
  // shows a clean access-denied panel (the backend 403s the data anyway).
  if (enginePerms.loaded && enginePerms.role !== 'admin' && !enginePerms.can('clients', 'view')) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]" data-testid="clients-access-denied">
        <div className="text-center max-w-sm">
          <Lock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="font-medium text-slate-700">You don't have access to Clients</p>
          <p className="text-sm text-slate-500 mt-1">This section requires the <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">clients:view</code> capability. Contact an administrator if you need access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      <PendingApprovalBanner kind="clients" />
      <div className="page-header">
        <div>
          <h1 className="page-title">Clients</h1>
          <p className="text-slate-500 mt-1">Customer master records — the parent accounts jobs are billed under</p>
        </div>
        <RequireCap module="clients" action="add">
          <Button className="btn-navy" onClick={openCreate} data-testid="add-client-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Client
          </Button>
        </RequireCap>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name, customer #, or city..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="client-search-input"
          />
        </div>
        <div className="w-40">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="client-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading clients...</div>
          ) : filteredClients.length === 0 ? (
            <div className="p-12 text-center" data-testid="clients-empty-state">
              <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">{search ? 'No clients match your search' : 'No clients yet'}</p>
              {!search && canAdd && <p className="text-sm text-slate-400 mt-1">Add a client or run the WinTeam AR import.</p>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer #</TableHead>
                    <TableHead>Client Name</TableHead>
                    <TableHead className="hidden md:table-cell">City / State</TableHead>
                    <TableHead className="hidden lg:table-cell">Primary Contact</TableHead>
                    <TableHead className="hidden lg:table-cell">Terms</TableHead>
                    <TableHead>Tax</TableHead>
                    <TableHead className="hidden md:table-cell">Contacts</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((c, i) => (
                    <TableRow key={c.id} data-testid={`client-row-${i}`}>
                      <TableCell className="font-mono text-xs">{c.customer_number}</TableCell>
                      <TableCell className="font-medium">
                        {c.name}
                        {!c.is_active && <Badge variant="secondary" className="ml-2 text-xs">Inactive</Badge>}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-slate-600">
                        {[c.city, c.state].filter(Boolean).join(', ') || '—'}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-slate-600">
                        {[c.contact_first_name, c.contact_last_name].filter(Boolean).join(' ') || '—'}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-slate-600">{c.terms || '—'}</TableCell>
                      <TableCell>
                        <Badge className={c.tax_status === 'exempt'
                          ? 'bg-amber-100 text-amber-800 hover:bg-amber-100'
                          : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100'}>
                          {c.tax_status === 'exempt' ? 'Exempt' : 'Taxable'}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-slate-600">
                        {(c.additional_contacts || []).length || '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openView(c)} data-testid={`client-view-btn-${i}`} title="View">
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canManageTeams && (
                            <Button variant="ghost" size="sm" onClick={() => { setTeamClient(c); setTeamOpen(true); }}
                              data-testid={`client-team-btn-${i}`} title="Supervisory team">
                              <ShieldCheck className="w-4 h-4 text-slate-600" />
                            </Button>
                          )}
                          <RequireCap module="clients" action="edit">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`client-edit-btn-${i}`} title="Edit">
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </RequireCap>
                          <RequireCap module="clients" action="edit">
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => handleToggleActive(c)}
                              data-testid={`client-archive-btn-${i}`}
                              title={c.is_active !== false ? 'Deactivate (archive)' : 'Reactivate'}
                            >
                              {c.is_active !== false
                                ? <Archive className="w-4 h-4 text-amber-600" />
                                : <ArchiveRestore className="w-4 h-4 text-emerald-600" />}
                            </Button>
                          </RequireCap>
                          {user?.role === 'admin' && (
                            <RequireCap module="clients" action="delete">
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(c)} data-testid={`client-delete-btn-${i}`} title="Delete (admin only)">
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </RequireCap>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ClientViewDialog
        client={viewingClient}
        open={showViewDialog}
        onOpenChange={setShowViewDialog}
        onEdit={editFromView}
        canEdit={canEdit}
      />

      <ClientTeamDialog
        client={teamClient}
        open={teamOpen}
        onOpenChange={setTeamOpen}
        canEdit={canEdit}
      />

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="client-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Client' : 'Add Client'}</DialogTitle>
            <DialogDescription>
              {editingId ? 'Update the client record.' : 'Create a new client (customer master) record.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Client Name *</Label>
                <Input value={form.name} onChange={(e) => setNameWithSuggest(e.target.value)} data-testid="client-name-input" />
              </div>
              <div>
                <Label>Customer Number *</Label>
                <Input
                  value={form.customer_number}
                  onChange={(e) => { setCnTouched(true); setForm({ ...form, customer_number: e.target.value.toUpperCase() }); }}
                  className="font-mono"
                  data-testid="client-customer-number-input"
                />
                {!editingId && !cnTouched && form.customer_number && (
                  <p className="text-xs text-slate-400 mt-1">Suggested from name — edit if needed</p>
                )}
              </div>
              <div>
                <Label>Date Entered</Label>
                <Input type="date" value={form.date_entered || ''} onChange={(e) => setForm({ ...form, date_entered: e.target.value })} data-testid="client-date-entered-input" />
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-slate-700 border-b border-slate-100 pb-1.5 mb-3">Address</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label>Address 1</Label>
                  <Input value={form.address1} onChange={(e) => setForm({ ...form, address1: e.target.value })} data-testid="client-address1-input" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Address 2</Label>
                  <Input value={form.address2} onChange={(e) => setForm({ ...form, address2: e.target.value })} data-testid="client-address2-input" />
                </div>
                <div>
                  <Label>City</Label>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="client-city-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>State</Label>
                    <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} maxLength={2} data-testid="client-state-input" />
                  </div>
                  <div>
                    <Label>Zip</Label>
                    <Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} data-testid="client-zip-input" />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-slate-700 border-b border-slate-100 pb-1.5 mb-3">Primary Contact</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <Input value={form.contact_first_name} onChange={(e) => setForm({ ...form, contact_first_name: e.target.value })} data-testid="client-contact-first-input" />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input value={form.contact_last_name} onChange={(e) => setForm({ ...form, contact_last_name: e.target.value })} data-testid="client-contact-last-input" />
                </div>
                <div>
                  <Label>Attention</Label>
                  <Input value={form.attention} onChange={(e) => setForm({ ...form, attention: e.target.value })} data-testid="client-attention-input" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone-input" />
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-slate-700 border-b border-slate-100 pb-1.5 mb-3">Billing</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Tax Status</Label>
                  <Select value={form.tax_status} onValueChange={(v) => setForm({ ...form, tax_status: v })}>
                    <SelectTrigger data-testid="client-tax-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="taxable">Taxable</SelectItem>
                      <SelectItem value="exempt">Exempt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.tax_status === 'exempt' && (
                  <div>
                    <Label>Tax Exempt ID</Label>
                    <Input value={form.tax_exempt_id} onChange={(e) => setForm({ ...form, tax_exempt_id: e.target.value })} data-testid="client-tax-exempt-id-input" />
                  </div>
                )}
                <div>
                  <Label>Terms</Label>
                  <Input value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} placeholder="e.g. Net 30 Days" data-testid="client-terms-input" />
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="client-active-switch" />
                  <Label>Active</Label>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between border-b border-slate-100 pb-1.5 mb-3">
                <h4 className="text-sm font-semibold text-slate-700">Additional Contacts / Invoice Recipients ({form.additional_contacts.length})</h4>
                <Button type="button" variant="outline" size="sm" onClick={addContact} data-testid="client-add-contact-btn">
                  <UserPlus className="w-4 h-4 mr-1.5" /> Add Contact
                </Button>
              </div>
              {form.additional_contacts.length === 0 && (
                <p className="text-sm text-slate-400">No additional contacts. Use these for extra invoice recipients.</p>
              )}
              <div className="space-y-3">
                {form.additional_contacts.map((ct, idx) => (
                  <div key={idx} className="p-3 rounded-lg border border-slate-200 bg-slate-50/60 relative" data-testid={`client-contact-editor-${idx}`}>
                    <button
                      type="button"
                      onClick={() => removeContact(idx)}
                      className="absolute top-2 right-2 p-1 rounded hover:bg-slate-200 text-slate-400"
                      data-testid={`client-remove-contact-btn-${idx}`}
                      title="Remove contact"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pr-6">
                      <Input placeholder="First name" value={ct.first_name || ''} onChange={(e) => updateContact(idx, 'first_name', e.target.value)} data-testid={`contact-${idx}-first-input`} />
                      <Input placeholder="Last name" value={ct.last_name || ''} onChange={(e) => updateContact(idx, 'last_name', e.target.value)} data-testid={`contact-${idx}-last-input`} />
                      <Input placeholder="Email" value={ct.email || ''} onChange={(e) => updateContact(idx, 'email', e.target.value)} data-testid={`contact-${idx}-email-input`} />
                      <Input placeholder="Phone 1" value={ct.phone1 || ''} onChange={(e) => updateContact(idx, 'phone1', e.target.value)} data-testid={`contact-${idx}-phone1-input`} />
                      <Input placeholder="Phone 2" value={ct.phone2 || ''} onChange={(e) => updateContact(idx, 'phone2', e.target.value)} data-testid={`contact-${idx}-phone2-input`} />
                      <Input placeholder="Address" value={ct.address || ''} onChange={(e) => updateContact(idx, 'address', e.target.value)} data-testid={`contact-${idx}-address-input`} />
                      <Input placeholder="City" value={ct.city || ''} onChange={(e) => updateContact(idx, 'city', e.target.value)} data-testid={`contact-${idx}-city-input`} />
                      <Input placeholder="State" value={ct.state || ''} onChange={(e) => updateContact(idx, 'state', e.target.value.toUpperCase())} maxLength={2} data-testid={`contact-${idx}-state-input`} />
                      <Input placeholder="Zip" value={ct.zip_code || ''} onChange={(e) => updateContact(idx, 'zip_code', e.target.value)} data-testid={`contact-${idx}-zip-input`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} data-testid="client-notes-input" />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="client-cancel-btn">Cancel</Button>
              <Button className="btn-navy" onClick={handleSave} disabled={saving} data-testid="save-client-btn">
                {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Client'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
